// ============================================================
//  Logica de calcul a aplicației Chit
//  (solduri + decontare optimă)
// ============================================================

// Rotunjim la 2 zecimale ca să evităm erorile de virgulă mobilă
// (ex: 0.1 + 0.2 = 0.30000000000000004 în JavaScript).
function rotunjeste(valoare) {
  return Math.round(valoare * 100) / 100;
}

// ------------------------------------------------------------
//  Ajutoare pentru împărțirea flexibilă a unei cheltuieli.
// ------------------------------------------------------------

// Împarte o sumă EGAL între mai mulți membri, corectând restul
// de la rotunjire ca totalul să iasă exact (ex: 100/3 = 33.33*3 = 99.99).
export function imparteEgal(total, memberIds) {
  if (memberIds.length === 0) return [];
  const cota = rotunjeste(total / memberIds.length);
  const splits = memberIds.map((id) => ({ memberId: id, amount: cota }));
  return ajusteazaRest(splits, total);
}

// Adaugă mica diferență de rotunjire pe ultima parte,
// astfel încât suma tuturor părților să fie exact `total`.
export function ajusteazaRest(splits, total) {
  if (splits.length === 0) return splits;
  const sumaCurenta = rotunjeste(splits.reduce((s, x) => s + x.amount, 0));
  const diferenta = rotunjeste(total - sumaCurenta);
  if (diferenta !== 0) {
    const ultim = splits[splits.length - 1];
    ultim.amount = rotunjeste(ultim.amount + diferenta);
  }
  return splits;
}

// Întoarce cât datorează fiecare membru pentru o cheltuială: [{ memberId, amount }].
// Suportă și cheltuielile din Etapa 1 (aveau doar `splitBetween`, împărțire egală).
export function getSplits(expense) {
  if (expense.splits && expense.splits.length > 0) {
    return expense.splits;
  }
  const ids = expense.splitBetween || [];
  return imparteEgal(expense.amount, ids);
}

// ------------------------------------------------------------
//  1. Calculează soldul net al fiecărui membru.
//
//  Sold POZITIV = a plătit mai mult decât partea sa
//                 -> e "pe plus", i se datorează bani.
//  Sold NEGATIV = a plătit mai puțin decât partea sa
//                 -> e "pe minus", datorează bani.
//
//  `payments` = plățile de decontare deja făcute (marcate ca „plătit").
// ------------------------------------------------------------
export function calculeazaSolduri(members, expenses, payments = []) {
  // Pornim cu soldul 0 pentru fiecare membru.
  const solduri = {};
  members.forEach((m) => {
    solduri[m.id] = 0;
  });

  expenses.forEach((chelt) => {
    // Persoana care a plătit adaugă la soldul ei tot ce a scos din buzunar.
    if (solduri[chelt.paidBy] !== undefined) {
      solduri[chelt.paidBy] += chelt.amount;
    }

    // Fiecare participant "consumă" partea lui (care poate fi inegală).
    getSplits(chelt).forEach((s) => {
      if (solduri[s.memberId] !== undefined) {
        solduri[s.memberId] -= s.amount;
      }
    });
  });

  // Plățile de decontare: `from` a dat bani lui `to`.
  // -> soldul lui `from` crește (se apropie de 0), al lui `to` scade.
  payments.forEach((p) => {
    if (solduri[p.from] !== undefined) solduri[p.from] += p.amount;
    if (solduri[p.to] !== undefined) solduri[p.to] -= p.amount;
  });

  // Rotunjim toate soldurile la final.
  Object.keys(solduri).forEach((id) => {
    solduri[id] = rotunjeste(solduri[id]);
  });

  return solduri; // ex: { "id1": 80, "id2": -80 }
}

// ------------------------------------------------------------
//  2. Calculează decontarea (transferurile dintre membri).
//
//  Algoritm GREEDY (lacom), pas cu pas:
//   a) Împărțim membrii în CREDITORI (sold +) și DEBITORI (sold -).
//   b) Îi sortăm descrescător după sumă.
//   c) Potrivim mereu cel mai mare creditor cu cel mai mare debitor
//      și transferăm între ei minimul dintre cele două sume.
//   d) Repetăm până când toate datoriile sunt stinse.
//
//  Rezultat: cine dă bani, cui și cât -> ex: "Andrei îi dă lui Maria 80 RON".
//
//  Notă: metoda greedy reduce puternic numărul de transferuri și, în
//  aproape toate cazurile reale, dă chiar numărul minim. Există cazuri
//  rare, construite special, în care ar putea exista o soluție cu un
//  transfer mai puțin — dar rămâne o metodă simplă, rapidă și clară.
// ------------------------------------------------------------
export function calculeazaDecontari(solduri) {
  const creditori = []; // au bani de primit (sold pozitiv)
  const debitori = []; // au bani de dat (sold negativ)

  Object.entries(solduri).forEach(([id, sold]) => {
    if (sold > 0.01) {
      creditori.push({ id, suma: sold });
    } else if (sold < -0.01) {
      // Stocăm datoria ca valoare pozitivă, e mai ușor de lucrat cu ea.
      debitori.push({ id, suma: -sold });
    }
    // Cei cu sold ~0 sunt deja "chit", îi ignorăm.
  });

  // Sortăm descrescător ca să potrivim mereu cele mai mari valori.
  creditori.sort((a, b) => b.suma - a.suma);
  debitori.sort((a, b) => b.suma - a.suma);

  const transferuri = [];
  let i = 0; // indexul creditorului curent
  let j = 0; // indexul debitorului curent

  while (i < creditori.length && j < debitori.length) {
    const creditor = creditori[i];
    const debitor = debitori[j];

    // Transferăm minimul dintre cât are creditorul de primit
    // și cât are debitorul de dat.
    const suma = rotunjeste(Math.min(creditor.suma, debitor.suma));

    if (suma > 0) {
      transferuri.push({
        deLa: debitor.id, // cine dă banii
        catre: creditor.id, // cine primește banii
        suma: suma,
      });
    }

    // Scădem suma transferată din ambele conturi.
    creditor.suma = rotunjeste(creditor.suma - suma);
    debitor.suma = rotunjeste(debitor.suma - suma);

    // Dacă un creditor a fost plătit complet, trecem la următorul.
    if (creditor.suma < 0.01) i++;
    // Dacă un debitor și-a achitat datoria, trecem la următorul.
    if (debitor.suma < 0.01) j++;
  }

  return transferuri; // ex: [{ deLa: "id1", catre: "id2", suma: 80 }]
}

// Formatează o sumă în lei românești (ex: 80 -> "80,00 RON").
export function formateazaRON(suma) {
  return (
    new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(suma) + " RON"
  );
}
